import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Meta, Title } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root',
})
export class MetaDataService {
  constructor(
    private titleService: Title,
    private metaService: Meta,
    private translateService: TranslateService
  ) { }

  setMetadata(title: string, keywords: string, description: string) {
    const translateMetadata: PageMetadata = this.translateMetadata({
      title,
      description,
      keywords,
    });
    this.setTitle(translateMetadata.title);
    this.setMetaTags(translateMetadata);
  }

  setOpenGraph(description: string, imgUrl: string, path: string) {
    const title = this.titleService.getTitle();
    const translateMetadata: PageMetadata = this.translateMetadata({ title, description });
    this.metaService.updateTag({ property: 'og:image', content: imgUrl });
    this.metaService.updateTag({ property: 'og:title', content: translateMetadata.title });
    this.metaService.updateTag({ property: 'og:description', content: translateMetadata.description });
    this.metaService.updateTag({ property: 'og:url', content: path });
    this.metaService.updateTag({ property: 'og:type', content: "website" });
    this.metaService.updateTag({ property: 'og:site_name', content: "Racebet365" });
    this.metaService.updateTag({ property: 'og:locale', content: 'en_US' });
  }

  setTwitterCard() {
    this.metaService.addTags([
      { name: 'twitter:card', content: 'summary' },
    ]);
  }

  setCanonicalUrl(canonicalUrl: string) {
    this.metaService.removeTag('name="canonical"');
    this.metaService.addTag({
      name: 'canonical',
      content: canonicalUrl,
    });
    this.createHrefLangLinkElement(canonicalUrl, 'en');
  }

  private setTitle(title: string) {
    this.titleService.setTitle(title);
  }

  private setMetaTags(metaData: PageMetadata) {
    this.metaService.updateTag({
      name: 'keywords',
      content: metaData.keywords,
    });
    this.metaService.updateTag({
      name: 'description',
      content: metaData.description,
    });
  }

  private translateMetadata(metaData: PageMetadata): PageMetadata {
    let translateMetadata: PageMetadata = { ...metaData };
    translateMetadata.title = this.translateService.instant(metaData.title);
    translateMetadata.description = this.translateService.instant(
      metaData.description
    );
    if (metaData.keywords) {
      translateMetadata.keywords = this.translateService.instant(
        metaData.keywords
      );
    }
    return translateMetadata;
  }

  generateHreflangTags(url: any) {
    const availableLanguages = ['en', 'fr']; // Update with your available languages
    const currentLanguage = localStorage.getItem('language');
    availableLanguages.forEach((lang) => {
      if (lang !== currentLanguage) {
        this.createHrefLangLinkElement(url, lang);
      }
    });
  }

  createHrefLangLinkElement(href: any, lang: any, rel: string = 'alternate') {
    this.deleteExistingHrefLangLink(lang, rel);
    const linkTag = document.createElement('link');
    linkTag.rel = rel;
    linkTag.href = href;
    linkTag.hreflang = Lang[lang];

    document.head.appendChild(linkTag);
  }

  deleteExistingHrefLangLink(lang: any, rel: string = 'alternate') {
    // Remove existing hreflang link if it exists
    const existingLink = document.querySelector(
      `link[rel="${rel}"][hreflang="${Lang[lang]}"]`
    );
    if (existingLink) {
      existingLink.remove();
    }
  }
}

export interface PageMetadata {
  title: string;
  description: string;
  keywords?: string;
}
export enum Lang {
  fr = 'fr-FR',
  en = 'en-EN',
}
